import Divider from "@mui/material/Divider";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem, { type MenuItemProps } from "@mui/material/MenuItem";
import { cloneElement, createContext, isValidElement, useContext, useMemo, useState, type MouseEvent, type ReactElement, type ReactNode } from "react";

interface DropdownContextValue { anchorEl: HTMLElement | null; close(): void; open(event: MouseEvent<HTMLElement>): void }
const DropdownContext = createContext<DropdownContextValue | null>(null);

export function DropdownMenu({ children }: { children: ReactNode }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const value = useMemo<DropdownContextValue>(() => ({ anchorEl, close: () => setAnchorEl(null), open: (event) => setAnchorEl(event.currentTarget) }), [anchorEl]);
  return <DropdownContext.Provider value={value}>{children}</DropdownContext.Provider>;
}

export function DropdownMenuTrigger({ children }: { asChild?: boolean; children: ReactNode }) {
  const context = useContext(DropdownContext);
  if (!context || !isValidElement(children)) return children;
  const child = children as ReactElement<{ onClick?: (event: MouseEvent<HTMLElement>) => void }>;
  return cloneElement(child, {
    "aria-haspopup": "menu",
    "aria-expanded": Boolean(context.anchorEl),
    onClick: (event: MouseEvent<HTMLElement>) => { child.props.onClick?.(event); if (!event.defaultPrevented) context.open(event); }
  } as Record<string, unknown>);
}

export function DropdownMenuContent({ children, className, align = "end" }: { children: ReactNode; className?: string; sideOffset?: number; align?: "start" | "center" | "end" }) {
  const context = useContext(DropdownContext);
  if (!context) return null;
  const horizontal = align === "start" ? "left" : align === "end" ? "right" : "center";
  return <Menu anchorEl={context.anchorEl} open={Boolean(context.anchorEl)} onClose={context.close} anchorOrigin={{ vertical: "bottom", horizontal }} transformOrigin={{ vertical: "top", horizontal }} slotProps={{ paper: { className: ["mui-app-menu", className].filter(Boolean).join(" ") }, list: { dense: true } }}>{children}</Menu>;
}

export function DropdownMenuGroup({ children }: { children: ReactNode }) { return <>{children}</>; }
export function DropdownMenuLabel({ children, className }: { children: ReactNode; className?: string }) { return <ListSubheader className={className} component="div">{children}</ListSubheader>; }

interface DropdownMenuItemProps extends Omit<MenuItemProps, "onSelect"> { onSelect?: () => void }
export function DropdownMenuItem({ children, className, onSelect, onClick, ...props }: DropdownMenuItemProps) {
  const context = useContext(DropdownContext);
  return <MenuItem className={className} onClick={(event) => { onClick?.(event); if (!event.defaultPrevented) { onSelect?.(); context?.close(); } }} {...props}>{children}</MenuItem>;
}

export function DropdownMenuSeparator({ className }: { className?: string }) { return <Divider className={className} />; }
